import React from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, ArrowRight } from "lucide-react";

const SHOTS = [
    {
        src: "/images/screenshots/auditor-portal.png",
        alt: "AuditVanguard Auditor Portal showing an engagement overview with review progress, open flags and a weekly digest",
        caption: "Auditor Portal — read-only evidence review",
    },
    {
        src: "/images/screenshots/magic-mapped-control.png",
        alt: "A Magic-Mapped control: AC-01 Access Control cross-mapped to SOC 2 CC6.1 and ISO 27001 A.5.15 with implementation notes and attached evidence",
        caption: "Magic-Mapped control with evidence",
    },
    {
        src: "/images/screenshots/report-cover.png",
        alt: "Branded PDF compliance audit report cover generated by AuditVanguard, prepared for auditor handover",
        caption: "Branded PDF export",
    },
];

export const FounderSection = () => (
    <section className="max-w-7xl mx-auto px-5 sm:px-10 py-16 sm:py-24" data-testid="founder-section">
        <div className="rounded-2xl border border-border bg-card p-6 sm:p-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <div
                className="h-24 w-24 sm:h-28 sm:w-28 shrink-0 rounded-2xl bg-foreground text-background grid place-items-center"
                role="img"
                aria-label="Photo of Godwin Oiku, founder"
                data-testid="founder-avatar"
            >
                <span className="font-display text-2xl font-semibold tracking-wide" aria-hidden="true">GO</span>
            </div>
            <div className="min-w-0">
                <div className="mono-label mb-2 inline-flex items-center gap-2">
                    <BadgeCheck className="h-3 w-3" strokeWidth={1.5} aria-hidden="true" /> Built by Godwin Oiku
                </div>
                <p className="text-base sm:text-lg leading-relaxed" data-testid="founder-bio-line1">
                    AuditVanguard is designed, built and operated by Godwin Oiku, a cybersecurity
                    practitioner with 12+ years across IT and security.
                </p>
                <p className="mt-1 text-base text-muted-foreground" data-testid="founder-bio-line2">
                    PMP · CompTIA Security+ · CySA+ certified.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-5">
                    <Link
                        to="/about"
                        className="tap-target inline-flex items-center gap-1.5 text-sm font-medium underline underline-offset-4 hover:opacity-80 transition-opacity rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        data-testid="founder-about-link"
                    >
                        Meet the team <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" />
                    </Link>
                    <a
                        href="https://www.linkedin.com/in/goddy83/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tap-target inline-flex items-center gap-1.5 text-sm font-medium underline underline-offset-4 hover:opacity-80 transition-opacity rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        data-testid="founder-linkedin-link"
                    >
                        LinkedIn
                    </a>
                </div>
            </div>
        </div>

        <h2 className="mt-14 font-display text-base md:text-lg font-semibold tracking-tight">
            Inside the product
        </h2>
        <p className="mt-2 text-base text-muted-foreground max-w-2xl">
            Real screens from the live product — swipe through on mobile.
        </p>
        <div
            className="mt-6 flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-none -mx-5 px-5 pb-2 md:grid md:grid-cols-3 md:overflow-visible md:mx-0 md:px-0"
            data-testid="product-shots-carousel"
            aria-label="Product screenshots"
        >
            {SHOTS.map((s) => (
                <figure
                    key={s.src}
                    className="snap-center shrink-0 w-[86%] md:w-auto rounded-2xl border border-border bg-card overflow-hidden"
                >
                    <div className="aspect-[16/10] overflow-hidden bg-muted">
                        <img src={s.src} alt={s.alt} loading="lazy" className="w-full h-full object-cover object-top" />
                    </div>
                    <figcaption className="px-4 py-3 text-sm text-muted-foreground">{s.caption}</figcaption>
                </figure>
            ))}
        </div>
    </section>
);
